import React, { useMemo, useState } from 'react';
import WizardShell from '../ui/wizard-shell';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import type { TrialLessonDTO } from '../../lib/trials';
import { type MessageDraft, markTrialNoOccurrence } from '../../lib/trials';
import { toast } from 'sonner';
import CommunicationComposer, { type RecipientGroup } from '../communications/CommunicationComposer';
import CommunicationStandortHint from '../communications/CommunicationStandortHint';
import { WizardCard, WizardSection, WizardKVGrid, WizardKVItem, WizardYesNoBadge } from '../ui/wizard-layout';

export type NoOccurrenceReasonKey = 'student_cancelled' | 'teacher_cancelled' | 'no_show' | 'unknown';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trial: TrialLessonDTO;
  standortId?: number;
  onUpdated?: (trial: TrialLessonDTO) => void;
};
type StepState = 1 | 2 | 3;
type StepDelta = -1 | 1;

export default function MarkNoOccurrenceWizardModal({ open, onOpenChange, trial, standortId, onUpdated }: Readonly<Props>) {
  const [step, setStep] = useState<StepState>(1);
  // Der bearbeitete Text aus dem Editor. Bisher ging stattdessen ein fester Satz raus (T-0067).
  const [entwurf, setEntwurf] = useState<MessageDraft | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const moveStep = (delta: StepDelta) => {
    setStep((prev) => {
      const next = (prev + delta) as StepState;
      return Math.min(3, Math.max(1, next)) as StepState;
    });
  };

  const [reasonKey, setReasonKey] = useState<NoOccurrenceReasonKey>('student_cancelled');
  const [reasonText, setReasonText] = useState('');

  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(false);

  const reasonLabel = useMemo(() => {
    const base = (() => {
      if (reasonKey === 'student_cancelled') return 'Schüler hat abgesagt';
      if (reasonKey === 'teacher_cancelled') return 'Lehrer hat abgesagt';
      if (reasonKey === 'no_show') return 'Schüler war nicht anwesend';
      return 'unbekannter Grund';
    })();
    const extra = reasonText.trim();
    return extra ? `${base}: ${extra}` : base;
  }, [reasonKey, reasonText]);

  const composerGroups: RecipientGroup[] = useMemo(() => {
    const sp = String((trial as any)?.subprofileId ?? (trial as any)?.subprofile_id ?? '').trim();
    if (!sp) return [];
    let wpUserId = 0;
    let subIndex: number | null = null;
    if (sp.includes('-')) {
      const [a, b] = sp.split('-', 2);
      wpUserId = Number.parseInt(a, 10) || 0;
      subIndex = b ? (Number.parseInt(b, 10) || null) : null;
    } else {
      wpUserId = Number.parseInt(sp, 10) || 0;
    }
    if (!wpUserId) return [];
    return [{
      key: 'student',
      label: 'Teilnehmer',
      recipients: [{ id: `person:${wpUserId}:${subIndex ?? ''}`, type: 'person' as const, wpUserId, subIndex, name: trial.studentName || 'Teilnehmer' }],
      templateKeys: { email: 'trial.no_occurrence.student', sms: 'trial.no_occurrence.student' },
      vars: { reason: reasonLabel },
      defaultSelectedChannels: { email: sendEmail, sms: sendSms },
    }];
  }, [trial, reasonLabel, sendEmail, sendSms]);

  const submit = async () => {
    setIsSubmitting(true);
    try {
      const res = await markTrialNoOccurrence(trial.id, {
        reasonKey,
        reasonText: reasonText.trim() || undefined,
        standortId,
        channels: { email: sendEmail, sms: sendSms },
        drafts: entwurf ? { student: entwurf } : undefined,
        reason: reasonLabel,
        notify: { student: true, teacher: false },
      });
      const failed = [sendEmail && (res.email as any)?.ok === false ? 'E-Mail' : null, sendSms && (res.sms as any)?.ok === false ? 'SMS' : null].filter(Boolean);
      if (failed.length) toast.warning(`Status gesetzt; Versand fehlgeschlagen: ${failed.join(', ')}`);
      else toast.success('Status „Termin hat nicht stattgefunden" wurde gesetzt');
      onUpdated?.(res.trial);
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message || 'Speichern fehlgeschlagen');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <WizardShell
      open={open}
      onOpenChange={onOpenChange}
      title="Termin hat nicht stattgefunden"
      description={`Schritt ${step} von 3`}
      currentStep={step}
      steps={[{ label: 'Grund' }, { label: 'Nachricht' }, { label: 'Übersicht' }]}
      onBack={step > 1 ? () => moveStep(-1) : undefined}
      canBack={step > 1 && !isSubmitting}
      onNext={step < 3 ? () => moveStep(1) : undefined}
      canNext={!isSubmitting && step < 3}
      onPrimary={step === 3 ? submit : undefined}
      primaryLabel={isSubmitting ? 'Speichere…' : 'Eintragen'}
      canPrimary={step === 3 && !isSubmitting}
      isLoading={isSubmitting}
    >
      <div className="space-y-4 py-4 motion-safe:animate-fade-up">
        {step === 1 && (
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Warum fand der Termin nicht statt?</Label>
              <Select value={reasonKey} onValueChange={(v) => setReasonKey(v as NoOccurrenceReasonKey)}>
                <SelectTrigger><SelectValue placeholder="Bitte wählen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="student_cancelled">Schüler hat abgesagt</SelectItem>
                  <SelectItem value="teacher_cancelled">Lehrer hat abgesagt</SelectItem>
                  <SelectItem value="no_show">Schüler war nicht anwesend</SelectItem>
                  <SelectItem value="unknown">unbekannter Grund</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Details (optional)</Label>
              <Textarea value={reasonText} onChange={(e) => setReasonText(e.target.value)} placeholder="Freitext…" />
            </div>
          </div>
        )}

        {step === 2 && composerGroups.length > 0 && standortId > 0 && (
          <CommunicationComposer
            active={open && step === 2}
            standortId={standortId}
            groups={composerGroups}
            onDraftsChange={({ groups: g }) => {
              const s = g['student'];
              if (s) {
                setSendEmail(!!s.selectedChannels.email);
                setSendSms(!!s.selectedChannels.sms);
                setEntwurf({
                  subject: s.email.subjectTpl || '',
                  html: s.email.htmlTpl || '',
                  text: s.email.textTpl || '',
                  sms: s.sms.smsTpl || '',
                });
              }
            }}
          />
        )}
        {step === 2 && composerGroups.length > 0 && standortId <= 0 ? (
          <CommunicationStandortHint />
        ) : null}

        {step === 3 && (
          <WizardCard>
            <WizardSection title="Zusammenfassung">
              <WizardKVGrid>
                <WizardKVItem label="Grund">{reasonLabel}</WizardKVItem>
              </WizardKVGrid>
            </WizardSection>
            <WizardSection title="Benachrichtigungen">
              <WizardKVGrid>
                <WizardKVItem label="E-Mail"><WizardYesNoBadge yes={sendEmail} /></WizardKVItem>
                <WizardKVItem label="SMS"><WizardYesNoBadge yes={sendSms} /></WizardKVItem>
              </WizardKVGrid>
            </WizardSection>
          </WizardCard>
        )}
      </div>
    </WizardShell>
  );
}
